import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SearchableSelect,
  SearchableSelectProps
} from 'record-it-components/input/SearchableSelect/SearchableSelect';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { TimeTrackingActivityCompilation } from '../../../classes/entities/TimeTrackingActivityCompilation/timeTrackingActivityCompilationTypes';
import { TimeTrackingActivityCompilationUtils } from '../../../classes/entities/TimeTrackingActivityCompilation/TimeTrackingActivityCompilationUtils';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import {
  SelectEntityItem,
  useSelectEntityItems
} from '../../../hooks/entity/useSelectEntityItems/useSelectEntityItems';
import { UseSelectEntityItemsUtils } from '../../../hooks/entity/useSelectEntityItems/UseSelectEntityItemsUtils';

export type TimeTrackingActivityCompilationSelectProps = PropsWithoutChildren<
  Omit<
    SearchableSelectProps<unknown>,
    | 'getKeyOfItem'
    | 'getDisplayTextOfItem'
    | 'items'
    | 'value'
    | 'onValueChanged'
    | 'label'
  >
> & {
  value: string;
  onValueChanged: (value: string) => void;
  userGroupId: string;
  label?: string;
};

export const TimeTrackingActivityCompilationSelect: FunctionComponent<TimeTrackingActivityCompilationSelectProps> =
  React.memo((props) => {
    const { value, onValueChanged, userGroupId, ...remainingProps } =
      prepareProps({ props, ignoreChildren: true });

    const { t } = useTranslation();
    const compilations = useEntities(
      EntityName.TimeTrackingActivityCompilation,
      'getByUserGroupId',
      userGroupId
    );

    const useSelectEntityItemsReturnValue = useSelectEntityItems({
      value,
      entities: compilations,
      entityNotFoundText: t(
        'components.timeTrackingActivityCompilation.TimeTrackingActivityCompilationSelect.unknownItem'
      ),
      onValueChanged,
      getDisplayTextOfEntity:
        TimeTrackingActivityCompilationUtils.displayTextGetter,
      getKeyOfEntity: TimeTrackingActivityCompilationUtils.keyGetter,
      getValueOfEntity: UseSelectEntityItemsUtils.entityIdGetter
    });

    return (
      <SearchableSelect<SelectEntityItem<TimeTrackingActivityCompilation>>
        value={useSelectEntityItemsReturnValue.selectedItem}
        items={useSelectEntityItemsReturnValue.items}
        label={t(
          'components.timeTrackingActivityCompilation.TimeTrackingActivityCompilationSelect.label'
        )}
        onValueChanged={useSelectEntityItemsReturnValue.onSelectedItemChanged}
        getDisplayTextOfItem={
          useSelectEntityItemsReturnValue.getDisplayTextOfItem
        }
        getKeyOfItem={useSelectEntityItemsReturnValue.getKeyOfItem}
        data-testid="TimeTrackingActivityCompilationSelect.SearchableSelect"
        {...remainingProps}
      ></SearchableSelect>
    );
  });
